import { computed, onBeforeUnmount, onMounted, ref } from "vue";

/**
 * Add-to-home-screen handling.
 *
 * Two paths, because the platforms differ:
 *
 * - **Chromium** fires `beforeinstallprompt`. We suppress the browser's own
 *   banner, keep the event, and fire it when the athlete accepts ours.
 * - **iOS Safari never fires it** and has no programmatic install at all. The
 *   only option there is telling people where the button is, so iOS gets
 *   instructions rather than a button that cannot work.
 *
 * Nothing is shown when the app is already installed, and a dismissal is
 * remembered -- an install banner that reappears on every visit is the kind of
 * thing people learn to tap past without reading.
 */

const DISMISS_KEY = "carbdial.install.dismissed.v1";

/** The event is not in the standard DOM lib; this is the shape we rely on. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const displayMode =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  // iOS exposes its own flag rather than the display-mode query.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return displayMode || iosStandalone;
}

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Macintosh; the touch-point check separates it.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function useInstallPrompt() {
  const deferred = ref<InstallPromptEvent | null>(null);
  const dismissed = ref(wasDismissed());
  const installed = ref(isStandalone());
  const ios = ref(false);

  const onBeforeInstall = (event: Event) => {
    event.preventDefault();
    deferred.value = event as InstallPromptEvent;
  };
  const onInstalled = () => {
    installed.value = true;
    deferred.value = null;
  };

  /** Chromium: a real button. iOS: instructions, since no API exists. */
  const canPrompt = computed(() => deferred.value !== null);
  const visible = computed(
    () => !installed.value && !dismissed.value && (canPrompt.value || ios.value),
  );

  async function install(): Promise<"accepted" | "dismissed" | "unavailable"> {
    const event = deferred.value;
    if (!event) return "unavailable";
    await event.prompt();
    const { outcome } = await event.userChoice;
    deferred.value = null;
    if (outcome === "dismissed") dismiss();
    return outcome;
  }

  function dismiss() {
    dismissed.value = true;
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Storage unavailable: the banner reappears next visit. Acceptable.
    }
  }

  onMounted(() => {
    installed.value = isStandalone();
    ios.value = isIos();
    if (typeof window === "undefined") return;
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
  });

  onBeforeUnmount(() => {
    if (typeof window === "undefined") return;
    window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    window.removeEventListener("appinstalled", onInstalled);
  });

  return { visible, canPrompt, ios, installed, dismissed, install, dismiss };
}
